import React, { useEffect, useState } from "react";
import { getAvailableTags } from "../../Api/TagsApi";
import MultiSelectDropdown from "../Overview/components/MultiSelectDropdown";
import AppCache from "../../Helper/Common/AppCache";

const DEFAULT_SELECTION = ["operational", "non_operational"];

/**
 * OperationalStatusMultiSelect - Shared header dropdown for the operational
 * status global filter (Active/Resolved/Analytics tabs in BMS Faults and FOP).
 *
 * Fetches options from getAvailableTags() internally and handles
 * label ↔ valueCode conversion so the parent only deals with valueCodes.
 *
 * Props:
 *   selectedValues    - currently selected valueCodes (string[])
 *   onSelectionChange - called with updated valueCodes when Apply is clicked
 *   onOptionsLoaded   - called once on mount with (allValueCodes, defaultSelection)
 *                       so the parent can initialise globalFilters
 *   onOptionsError    - optional; called when the options fail to load, so a parent
 *                       that waits for onOptionsLoaded can stop waiting
 */
const OperationalStatusMultiSelect = ({ selectedValues = [], onSelectionChange, onOptionsLoaded, onOptionsError }) => {
  const [optionObjects, setOptionObjects] = useState([]);
  const [displayOptions, setDisplayOptions] = useState([]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await getAvailableTags();
        const tags = res?.response?.data?.tags || [];
        const statusTag = tags.find(tag => tag.tagCode === "status");
        const rawList = statusTag?.supportedValues || [];

        const preAppliedGlobalFilters = AppCache.getItem("preAppliedGlobalFilters") || {};
        const excludeValues = preAppliedGlobalFilters["inventory.digitalTwinListTable"]?.excludeOperationalStatus || [];
        const list = excludeValues.length
          ? rawList.filter(item => !excludeValues.includes(item.valueCode))
          : rawList;

        setOptionObjects(list);
        setDisplayOptions(list.map(item => item.displayName));

        if (onOptionsLoaded) {
          const allValueCodes = list.map(item => item.valueCode);
          onOptionsLoaded(allValueCodes, DEFAULT_SELECTION);
        }
      } catch (e) {
        console.error("Error fetching operational status options", e);
        if (onOptionsError) onOptionsError(e);
      }
    };
    fetchOptions();
  }, []);

  const selectedLabels = selectedValues.map(code => {
    const option = optionObjects.find(opt => opt.valueCode === code);
    return option ? option.displayName : code;
  });

  const handleChange = (labels) => {
    const valueCodes = labels.map(label => {
      const option = optionObjects.find(opt => opt.displayName === label);
      return option ? option.valueCode : label;
    });
    if (onSelectionChange) {
      onSelectionChange(valueCodes);
    }
  };

  return (
    <MultiSelectDropdown
      selectedOptions={selectedLabels}
      options={displayOptions}
      label="Operational Status"
      onChange={handleChange}
      defaultAllSelected={false}
    />
  );
};

export default OperationalStatusMultiSelect;
